import { useMemo, useState } from "react";
import api from "@/lib/api";
import { pName, pMatch } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Camera, ChevronsUpDown, Check } from "lucide-react";
import { toast } from "sonner";
import { CameraScanner } from "./CameraScanner";

const hit = (p, s) => pMatch(p, s) || (p.barcode || "").toLowerCase().includes(s) || (p.barcodes || []).some((b) => String(b).toLowerCase().includes(s));

// Resolve a scanned/typed code to a product: exact barcode or SKU in the loaded list, else ask the server.
export async function findByCode(code, products = []) {
  const c = String(code || "").trim();
  if (!c) return null;
  const local = products.find((x) => x.is_active !== false && (x.barcode === c || x.sku === c || (x.barcodes || []).includes(c)));
  if (local) return local;
  try { const { data } = await api.get("/products", { params: { barcode: c } }); return (data || []).find((x) => x.is_active !== false) || null; } catch { return null; }
}

// Camera barcode button that resolves the code to a product and hands it back.
export function ScanButton({ products, onProduct, testId = "scan-btn" }) {
  const [cam, setCam] = useState(false);
  const onScan = async (code) => {
    const p = await findByCode(code, products);
    if (!p) return toast.error(`No product matches barcode ${code}`);
    onProduct(p);
  };
  return (
    <>
      <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0 text-emerald-600" title="Scan barcode with camera" onClick={() => setCam(true)} data-testid={testId}>
        <Camera className="h-4 w-4" />
      </Button>
      <CameraScanner open={cam} onOpenChange={setCam} onDetected={onScan} />
    </>
  );
}

// Enter in a search box: if the text is a barcode/SKU, pick that product (handheld scanners type + Enter).
export const scanOnEnter = (products, onProduct) => async (e) => {
  if (e.key !== "Enter") return;
  e.preventDefault();
  const code = e.currentTarget.value.trim();
  const p = await findByCode(code, products);
  if (p) onProduct(p);
  else if (code && !/\s/.test(code)) toast.error(`No product matches code ${code}`);
};

const StockTag = ({ n, testId }) => (
  <span className={`ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${n > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid={testId}>
    {n > 0 ? `${n} in stock` : "Out of stock"}
  </span>
);

// Searchable product picker: type name / code / barcode or scan with the camera.
export function ProductCombobox({ products, value, onChange, testId = "product-combobox", placeholder = "Search name, code or scan", allLabel, className = "", stock, includeInactive }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = products.find((p) => p.id === value);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const pool = allLabel || includeInactive ? products : products.filter((p) => p.is_active !== false || p.id === value);
    const rows = s ? pool.filter((p) => hit(p, s)) : pool;
    const sorted = stock ? [...rows].sort((x, y) => (stock[y.id] > 0) - (stock[x.id] > 0)) : rows;
    return sorted.slice(0, 60);
  }, [products, q, stock, allLabel, includeInactive, value]);
  const pick = (id) => { onChange(id); setOpen(false); setQ(""); };
  const label = selected ? `${pName(selected)} · ${selected.sku}` : allLabel && (!value || value === "all") ? allLabel : placeholder;
  return (
    <div className={`flex gap-1 min-w-0 w-full ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" className="h-9 flex-1 min-w-0 w-0 overflow-hidden justify-between font-normal" data-testid={testId}>
            <span className={`truncate ${selected || allLabel ? "" : "text-muted-foreground"}`}>{label}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[320px]" align="start">
          <Command shouldFilter={false}>
            <CommandInput value={q} onValueChange={setQ} placeholder="Name, code or barcode…" data-testid={`${testId}-search`} />
            <CommandList className="max-h-64">
              <CommandEmpty>No matching product</CommandEmpty>
              {allLabel && !q && <CommandItem value="all" onSelect={() => pick("all")} data-testid={`${testId}-opt-all`}>{allLabel}</CommandItem>}
              {list.map((p) => (
                <CommandItem key={p.id} value={p.id} onSelect={() => pick(p.id)} data-testid={`${testId}-opt-${p.sku}`}>
                  <Check className={`h-3.5 w-3.5 mr-1.5 ${p.id === value ? "opacity-100" : "opacity-0"}`} />
                  <span className={`truncate ${stock && !(stock[p.id] > 0) ? "text-muted-foreground" : ""}`}>{pName(p)}</span><span className="ml-auto pl-2 text-xs font-mono text-muted-foreground">{p.sku}</span>
                  {stock && <StockTag n={stock[p.id] || 0} testId={`${testId}-stock-${p.sku}`} />}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ScanButton products={products} testId={`${testId}-scan`} onProduct={(p) => { onChange(p.id); toast.success(`${pName(p)} selected`); }} />
    </div>
  );
}
